import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Phone, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

export default function RosieAutoQuote() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [messageTemplate, setMessageTemplate] = useState("");
  const [twilioNumber, setTwilioNumber] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.get(`${API}/settings/rosie-auto-quote`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnabled(res.data.enabled);
      setMessageTemplate(res.data.message_template);
      setTwilioNumber(res.data.twilio_number);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      await axios.put(
        `${API}/settings/rosie-auto-quote`,
        { enabled, message_template: messageTemplate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Settings saved!");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-32">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Settings</span>
        </button>

        <div className="mb-6">
          <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase">
            Rosie Auto-Quote
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Automatically send a quote link when Rosie notifies you of a new call
          </p>
        </div>

        {/* How it works */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">How it works</h3>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Someone calls your business and Rosie answers</li>
            <li>Rosie texts the caller's info to your Twilio number</li>
            <li>Your portal detects the "New call!" message and extracts the phone number</li>
            <li>The quote link text is automatically sent to the caller</li>
          </ol>
          <div className="mt-3 bg-blue-100 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-900">
              Have Rosie text notifications to:
            </p>
            <p className="text-lg font-bold text-blue-900 font-mono mt-1" data-testid="twilio-number">
              {twilioNumber || "Not configured"}
            </p>
          </div>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="bg-white rounded-xl border p-5 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-zinc-900">Auto-Quote Enabled</h3>
              <p className="text-sm text-zinc-500 mt-0.5">
                {enabled
                  ? "Callers will automatically receive a quote link text"
                  : "Auto-quote is turned off — Rosie notifications will be logged but no text will be sent"}
              </p>
            </div>
            <button
              data-testid="rosie-toggle"
              onClick={() => setEnabled(!enabled)}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                enabled ? "bg-[#207024]" : "bg-zinc-300"
              }`}
            >
              <div
                className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                  enabled ? "left-7" : "left-1"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Message Template */}
        <div className="bg-white rounded-xl border p-5 mb-6">
          <h3 className="font-semibold text-zinc-900 mb-1">Message Template</h3>
          <p className="text-sm text-zinc-500 mb-3">
            Use <code className="bg-zinc-100 px-1 py-0.5 rounded text-xs font-mono">{"{quote_link}"}</code> where you want the instant quote link to appear.
          </p>
          <textarea
            data-testid="rosie-message-template"
            value={messageTemplate}
            onChange={(e) => setMessageTemplate(e.target.value)}
            rows={10}
            className="w-full border border-zinc-200 rounded-lg p-3 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-[#207024]/30 focus:border-[#207024] resize-y"
          />
          <p className="text-xs text-zinc-400 mt-2">
            {messageTemplate.includes("{quote_link}")
              ? "Quote link placeholder detected"
              : "Warning: No {quote_link} placeholder found — the link won't be included in the text"}
          </p>
        </div>

        {/* Save Button */}
        <button
          data-testid="save-rosie-settings"
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#207024] text-white py-3 rounded-xl font-semibold hover:bg-[#185a1c] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
